import React from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { FiArrowRight, FiTrendingUp } from 'react-icons/fi'

/* ============================================================
 *  Trends / 美業預約平台趨勢
 * ============================================================ */

const Page = styled.div`
  background: var(--color-bg);
  color: var(--color-ink);
  min-height: 100vh;
`

const Hero = styled.section`
  padding: 9rem 1.5rem 4rem;

  @media (min-width: 1440px) {
    padding: 10rem 2rem 5rem;
  }
  @media (max-width: 768px) {
    padding: 6.5rem 1.25rem 1.5rem;
  }
`

const HeroInner = styled.div`
  max-width: var(--container-wide);
  margin: 0 auto;
  background: linear-gradient(160deg, var(--color-bg-soft) 0%, var(--color-bg) 100%);
  border: 1px solid var(--color-line);
  border-radius: var(--radius-xl);
  padding: 4rem 3.5rem;
  box-shadow: var(--shadow-card);

  @media (max-width: 768px) {
    padding: 2.75rem 1.75rem;
    border-radius: var(--radius-lg);
  }

  .tag {
    display: inline-block;
    font-size: 0.72rem;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: #d5728a;
    font-weight: 700;
    margin-bottom: 1rem;
  }
  h1 {
    font-family: 'Playfair Display', 'Noto Serif TC', serif;
    font-size: clamp(1.9rem, 5.5vw, 4.5rem);
    line-height: 1.1;
    font-weight: 700;
    color: #1b0d12;
    max-width: 900px;
    margin-bottom: 1.5rem;
    letter-spacing: -0.01em;
  }
  h1 em {
    font-style: italic;
    color: #d5728a;
    font-weight: 500;
  }
  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: #4b2b37;
    max-width: 680px;
  }

  @media (max-width: 640px) {
    padding: 1.75rem 1.25rem;
    h1 {
      margin-bottom: 1rem;
    }
    p {
      font-size: 0.95rem;
      line-height: 1.7;
    }
  }
`

const DividerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 2.5rem;
  font-size: 0.8rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #9a4c66;
  font-weight: 600;

  .line {
    flex: 1;
    height: 1px;
    background: #e7cfd7;
  }

  @media (max-width: 640px) {
    margin-top: 1.5rem;
    font-size: 0.7rem;
  }
`

const Section = styled.section`
  max-width: var(--container-wide);
  margin: 0 auto;
  padding: 3.5rem 1.5rem;

  @media (min-width: 1440px) {
    padding: 4rem 2rem;
  }
  @media (max-width: 768px) {
    padding: 2.5rem 1.25rem;
  }
`

const SectionHead = styled.div`
  max-width: 720px;
  margin-bottom: 2.25rem;

  .tag {
    display: inline-block;
    font-size: 0.72rem;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: #d5728a;
    font-weight: 700;
    margin-bottom: 1rem;
  }
  h2 {
    font-family: 'Playfair Display', serif;
    font-size: clamp(1.55rem, 3.5vw, 2.6rem);
    font-weight: 700;
    color: #1b0d12;
    margin-bottom: 1rem;
    line-height: 1.2;
  }
  p {
    font-size: 1.02rem;
    line-height: 1.8;
    color: #6b3547;
  }

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    p {
      font-size: 0.95rem;
      line-height: 1.7;
    }
  }
`

/* -------- Market stats -------- */
const MarketWrap = styled.section`
  padding: 1rem 1.5rem 2.5rem;

  @media (min-width: 1440px) {
    padding: 1rem 2rem 3rem;
  }
  @media (max-width: 768px) {
    padding: 0.5rem 1.25rem 1.5rem;
  }
`

const MarketInner = styled.div`
  max-width: var(--container-wide);
  margin: 0 auto;
  background: linear-gradient(160deg, #1b0d12 0%, #3d1f2a 100%);
  color: #fff;
  padding: 4rem 3rem;
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-heavy);

  @media (max-width: 768px) {
    padding: 2.25rem 1.25rem;
    border-radius: var(--radius-lg);
  }
`

const MarketHead = styled.div`
  max-width: 720px;
  margin-bottom: 3rem;

  .tag {
    display: inline-block;
    font-size: 0.72rem;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: #f5c6d3;
    font-weight: 700;
    margin-bottom: 1rem;
  }
  h2 {
    font-family: 'Playfair Display', serif;
    font-size: clamp(1.3rem, 3.5vw, 2.6rem);
    font-weight: 700;
    color: #fff;
    margin-bottom: 1rem;
    line-height: 1.25;
  }
  p {
    color: rgba(255, 255, 255, 0.78);
    line-height: 1.8;
  }

  @media (max-width: 640px) {
    margin-bottom: 1.5rem;
    p {
      font-size: 0.92rem;
      line-height: 1.7;
    }
  }
`

const MarketGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    gap: 0.6rem;
  }
`

const MarketCard = styled(motion.div)`
  padding: 2rem 1.75rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(245, 198, 211, 0.18);
  border-radius: var(--radius-lg);

  .num {
    white-space: nowrap;
    font-family: 'Playfair Display', serif;
    font-size: 2.5rem;
    color: #f5c6d3;
    font-weight: 700;
    line-height: 1;
    margin-bottom: 0.5rem;
  }
  .label {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.75);
    line-height: 1.55;
    margin-bottom: 0.75rem;
  }
  .source {
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.45);
    letter-spacing: 0.06em;
  }

  @media (max-width: 640px) {
    padding: 1.1rem 0.95rem;
    border-radius: var(--radius-md);
    .num {
      font-size: 1.55rem;
      margin-bottom: 0.4rem;
    }
    .label {
      font-size: 0.75rem;
      line-height: 1.5;
      margin-bottom: 0.5rem;
    }
    .source {
      font-size: 0.62rem;
      letter-spacing: 0.02em;
    }
  }
`

/* -------- Shifts -------- */
const ShiftList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  @media (max-width: 640px) {
    gap: 0.75rem;
  }
`

const ShiftRow = styled(motion.div)`
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 2rem;
  padding: 2.25rem 2.5rem;
  background: #fff;
  border: 1px solid var(--color-line);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--color-primary);
    box-shadow: var(--shadow-card-hover);
    transform: translateY(-2px);
  }

  .num {
    white-space: nowrap;
    font-family: 'Playfair Display', serif;
    font-size: 3rem;
    color: #d5728a;
    font-weight: 700;
    line-height: 1;
  }
  .body {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 1rem;
    }
  }
  h4 {
    font-family: 'Playfair Display', serif;
    font-size: 1.35rem;
    color: #1b0d12;
    font-weight: 700;
    margin-bottom: 0.75rem;
    line-height: 1.3;
  }
  p {
    font-size: 0.95rem;
    color: #6b3547;
    line-height: 1.7;
  }
  .insight {
    background: var(--color-bg-soft);
    padding: 1.5rem 1.75rem;
    border-radius: var(--radius-md);
    border-left: 3px solid var(--color-primary);
  }
  .insight strong {
    display: block;
    font-size: 0.7rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--color-primary);
    margin-bottom: 0.4rem;
    font-weight: 700;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    padding: 1.25rem 1.1rem;
    border-radius: var(--radius-md);

    .num {
      font-size: 1.8rem;
    }
    .body {
      gap: 0.75rem;
    }
    h4 {
      font-size: 1.05rem;
      margin-bottom: 0.4rem;
    }
    p {
      font-size: 0.85rem;
      line-height: 1.65;
    }
    .insight {
      padding: 0.9rem 1rem;
    }
  }
`

/* -------- Pain vs Solution -------- */
const CompareGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 640px) {
    gap: 0.75rem;
  }
`

const CompareCol = styled.div`
  padding: 2.75rem 2.25rem;
  background: ${(props) => (props.dark ? 'linear-gradient(160deg, #1b0d12 0%, #3d1f2a 100%)' : '#fff')};
  color: ${(props) => (props.dark ? '#fff' : 'var(--color-ink)')};
  border: 1px solid ${(props) => (props.dark ? '#1b0d12' : 'var(--color-line)')};
  border-radius: var(--radius-xl);
  box-shadow: ${(props) => (props.dark ? 'var(--shadow-heavy)' : 'var(--shadow-card)')};

  @media (max-width: 768px) {
    padding: 2rem 1.75rem;
    border-radius: var(--radius-lg);
  }

  .col-tag {
    display: inline-block;
    font-size: 0.72rem;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: ${(props) => (props.dark ? '#f5c6d3' : '#9a4c66')};
    font-weight: 700;
    margin-bottom: 0.75rem;
  }
  h4 {
    font-family: 'Playfair Display', serif;
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    line-height: 1.3;
  }
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  li {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    font-size: 0.95rem;
    line-height: 1.65;

    @media (max-width: 640px) {
      font-size: 0.86rem;
    }
    color: ${(props) => (props.dark ? 'rgba(255,255,255,0.85)' : '#1b0d12')};
  }
  li::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${(props) => (props.dark ? '#f5c6d3' : '#d5728a')};
    margin-top: 0.55rem;
    flex-shrink: 0;
  }

  @media (max-width: 640px) {
    padding: 1.5rem 1.25rem;
    border-radius: var(--radius-md);
    h4 {
      font-size: 1.15rem;
      margin-bottom: 1rem;
    }
    ul {
      gap: 0.6rem;
    }
  }
`

/* -------- Timeline -------- */
const TimelineWrap = styled.div`
  position: relative;
  padding: 3rem 3rem 3rem 4.5rem;
  background: #fff;
  border: 1px solid var(--color-line);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-card);
  display: flex;
  flex-direction: column;
  gap: 2.5rem;

  &::before {
    content: '';
    position: absolute;
    left: 3rem;
    top: 3rem;
    bottom: 3rem;
    width: 2px;
    background: var(--color-line);
  }

  @media (max-width: 768px) {
    padding: 2rem 1.75rem 2rem 3rem;
    border-radius: var(--radius-lg);

    &::before {
      left: 1.5rem;
      top: 2rem;
      bottom: 2rem;
    }
  }

  @media (max-width: 640px) {
    padding: 1.5rem 1.25rem 1.5rem 2.75rem;
    gap: 1.5rem;
    border-radius: var(--radius-md);

    &::before {
      left: 1.25rem;
      top: 1.5rem;
      bottom: 1.5rem;
    }
  }
`

const TimelineItem = styled.div`
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: -1.65rem;
    top: 0.35rem;
    width: 14px;
    height: 14px;
    background: var(--color-primary);
    border: 3px solid #fff;
    box-shadow: 0 0 0 2px var(--color-primary);
    border-radius: 50%;
  }

  @media (max-width: 768px) {
    &::before {
      left: -1.65rem;
    }
  }

  .year {
    font-family: 'Playfair Display', serif;
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--color-primary);
    margin-bottom: 0.5rem;
  }
  h5 {
    font-size: 1.15rem;
    font-weight: 700;
    color: var(--color-ink);
    margin-bottom: 0.5rem;
  }
  p {
    font-size: 0.95rem;
    line-height: 1.75;
    color: var(--color-muted);
  }

  @media (max-width: 640px) {
    &::before {
      left: -1.85rem;
      width: 12px;
      height: 12px;
    }
    .year {
      font-size: 1.15rem;
      margin-bottom: 0.3rem;
    }
    h5 {
      font-size: 1rem;
      margin-bottom: 0.3rem;
    }
    p {
      font-size: 0.85rem;
      line-height: 1.65;
    }
  }
`

/* -------- CTA -------- */
const CtaWrap = styled.section`
  max-width: var(--container-wide);
  margin: 0 auto;
  padding: 2rem 1.5rem 4rem;

  @media (min-width: 1440px) {
    padding: 2.5rem 2rem 5rem;
  }

  @media (max-width: 640px) {
    padding: 1rem 1.25rem 2.5rem;
  }
`

const CtaInner = styled.div`
  background: linear-gradient(135deg, #d5728a 0%, #b85578 100%);
  color: #fff;
  padding: 4rem 3rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  flex-wrap: wrap;
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-heavy);

  @media (max-width: 768px) {
    padding: 2.75rem 2rem;
    border-radius: var(--radius-lg);
  }

  h3 {
    font-family: 'Playfair Display', serif;
    font-size: clamp(1.4rem, 2.8vw, 2.2rem);
    font-weight: 700;
    margin-bottom: 0.75rem;
    line-height: 1.2;
  }
  p {
    font-size: 1rem;
    line-height: 1.7;
    color: rgba(255, 255, 255, 0.9);
    max-width: 520px;
  }

  @media (max-width: 640px) {
    padding: 2rem 1.5rem;
    gap: 1.25rem;
    p {
      font-size: 0.92rem;
    }
  }
`

const CtaBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #fff;
  color: var(--color-ink);
  border: none;
  padding: 1.05rem 2rem;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  border-radius: var(--radius-pill);
  transition: transform 0.25s ease;

  &:hover {
    transform: translateY(-2px);
  }

  @media (max-width: 640px) {
    padding: 0.85rem 1.5rem;
    font-size: 0.92rem;
  }
`

/* ============================================================
 *  Component
 * ============================================================ */

const Trends = () => {
  const navigate = useNavigate()

  const stats = [
    { num: '$4,830億', label: '2024 全球美業市場規模（美金）', source: 'McKinsey, 2024' },
    { num: '+6%', label: '美業預估年複合成長率（2024–2028）', source: 'Statista' },
    { num: '67%', label: '消費者偏好線上預約，而非電話或私訊', source: 'Mindbody 2024' },
    { num: '3.2x', label: '導入預約 SaaS 後，平均回客率提升倍數', source: 'Beautefeel 內部數據' }
  ]

  const shifts = [
    {
      num: '01',
      title: '預約決策在深夜完成',
      desc:
        '超過 40% 的預約發生在晚間 9 點到凌晨 1 點之間。消費者期待「隨時打開手機、15 秒完成預約」，而不是等店家上班時間回覆。',
      insight:
        '深夜預約已是主流，店家若仍依賴人工回覆，等同於關上一半的流量入口。'
    },
    {
      num: '02',
      title: '社群與預約無縫串接',
      desc:
        '消費者從 IG 看到作品、心動、到預約的旅程，越短越好。若中間需要跳轉多個平台或填寫繁複表單，轉換率會大幅下滑。',
      insight:
        'IG Link-in-bio 結合預約官網，已成為美業品牌流量變現的關鍵。'
    },
    {
      num: '03',
      title: '訂金制與套票制成為常態',
      desc:
        '疫情後，訂金收取不再被視為不禮貌。專業美業店家藉由線上收訂金大幅降低放鳥率，套票制更是鎖定長期顧客的標配。',
      insight:
        '預收款已從「可選擇」變成「應具備」，影響店家現金流甚鉅。'
    },
    {
      num: '04',
      title: '個人化體驗取代大眾行銷',
      desc:
        '消費者期待被記得：上次的偏好、過敏成分、喜歡的技術師。沒有 CRM 系統支撐的店家，難以在競爭中建立長期黏著度。',
      insight:
        '記得顧客名字只是基本，記得他偏好的燈泡暖度才是留住顧客的關鍵。'
    },
    {
      num: '05',
      title: '評價透明化成為選店關鍵',
      desc:
        'Google Map、Dcard、小紅書、IG 貼文。消費者在踏進店門前，已經完成了至少三次的比對與驗證。沒有口碑資產的品牌將越來越難被看見。',
      insight:
        '服務後自動邀請評價，是累積品牌信任的低成本、高報酬策略。'
    }
  ]

  return (
    <Page>
      {/* Hero */}
      <Hero>
        <HeroInner>
          <span className='tag'>Industry Insights · 2026</span>
          <h1>
            美業預約平台的<br />
            <em>下一個十年</em>
          </h1>
          <p>
            我們深入觀察亞太與全球市場的美業數位化進程，整理出消費者行為、產業數據、店家營運模式的轉變，
            幫助美業經營者在快速變動的產業中，看清自己的位置與下一步。
          </p>
          <DividerRow>
            <FiTrendingUp /> Yunyun Tech · 產業觀察報告
            <div className='line' />
          </DividerRow>
        </HeroInner>
      </Hero>

      {/* Market stats */}
      <MarketWrap>
        <MarketInner>
          <MarketHead>
            <span className='tag'>Market Size</span>
            <h2>
              美業，不只是美。<br />
              它是全球前五大消費產業之一
            </h2>
            <p>
              當「愛自己」成為新世代的主流敘事，美業的成長曲線，正以前所未有的速度向上延伸。
            </p>
          </MarketHead>
          <MarketGrid>
            {stats.map((s, i) => (
              <MarketCard
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
              >
                <div className='num'>{s.num}</div>
                <div className='label'>{s.label}</div>
                <div className='source'>來源：{s.source}</div>
              </MarketCard>
            ))}
          </MarketGrid>
        </MarketInner>
      </MarketWrap>

      {/* 5 shifts */}
      <Section>
        <SectionHead>
          <span className='tag'>Consumer Behavior</span>
          <h2>
            5 個正在發生的消費者行為轉變
          </h2>
          <p>
            這些轉變，不是會不會發生的問題，而是已經發生。能越早看到、越早調整的品牌，將在接下來 3 年拉開差距。
          </p>
        </SectionHead>

        <ShiftList>
          {shifts.map((s, i) => (
            <ShiftRow
              key={s.num}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
            >
              <div className='num'>{s.num}</div>
              <div className='body'>
                <div>
                  <h4>{s.title}</h4>
                  <p>{s.desc}</p>
                </div>
                <div className='insight'>
                  <strong>Insight</strong>
                  <p>{s.insight}</p>
                </div>
              </div>
            </ShiftRow>
          ))}
        </ShiftList>
      </Section>

      {/* Pain vs Solution */}
      <Section>
        <SectionHead>
          <span className='tag'>Pain vs. Solution</span>
          <h2>傳統經營痛點 × SaaS 時代的新解法</h2>
          <p>
            美業從來不缺好手藝，缺的是一個能把好手藝轉換成可持續營收的系統。
          </p>
        </SectionHead>

        <CompareGrid>
          <CompareCol>
            <span className='col-tag'>傳統經營困境</span>
            <h4>手工作業讓好技術被埋沒</h4>
            <ul>
              <li>預約散落在 LINE、IG 私訊、來電各處</li>
              <li>忘記客戶上次的服務或偏好，每次都像新客</li>
              <li>忘記提醒 → 放鳥 → 時段浪費</li>
              <li>無法掌握每月營收結構與熱門服務</li>
              <li>沒有會員機制，顧客做完就流失</li>
              <li>找助理幫忙 = 增加人力成本</li>
            </ul>
          </CompareCol>
          <CompareCol dark>
            <span className='col-tag'>Beautefeel SaaS 解方</span>
            <h4>讓你把時間還給專業，其餘交給系統</h4>
            <ul>
              <li>所有預約收攏至單一後台、多裝置同步</li>
              <li>完整顧客檔案，服務一打開就看見歷史紀錄</li>
              <li>自動化多通道提醒，放鳥率降至個位數</li>
              <li>即時儀表板，每日 3 分鐘掌握營運脈動</li>
              <li>會員點數、生日優惠、回訪提醒全自動</li>
              <li>一個系統 = 一位 24 小時不休息的店務助理</li>
            </ul>
          </CompareCol>
        </CompareGrid>
      </Section>

      {/* Timeline */}
      <Section>
        <SectionHead>
          <span className='tag'>Roadmap</span>
          <h2>美業預約數位化的演進軌跡</h2>
          <p>
            從紙本預約簿到 AI 智慧推薦，美業的預約管理走過了一條陡峭但必然的路。
          </p>
        </SectionHead>

        <TimelineWrap>
          <TimelineItem>
            <div className='year'>2010 – 2015</div>
            <h5>紙本 & 電話預約時代</h5>
            <p>
              所有預約寫在紙本簿、靠記憶與人力。放鳥沒有成本、時段無法被數據化，店家難以規模化。
            </p>
          </TimelineItem>
          <TimelineItem>
            <div className='year'>2016 – 2020</div>
            <h5>社群 & 表單預約時代</h5>
            <p>
              LINE 群組、Google 表單、IG 私訊成為主流。預約更便利，但資料散落、無法整合顧客歷程。
            </p>
          </TimelineItem>
          <TimelineItem>
            <div className='year'>2021 – 2024</div>
            <h5>SaaS 預約平台崛起</h5>
            <p>
              美業專用預約 SaaS 開始被大量採用。整合金流、CRM、行銷自動化，店家正式進入「數據驅動」的營運時代。
            </p>
          </TimelineItem>
          <TimelineItem>
            <div className='year'>2025 – 未來</div>
            <h5>AI 個人化 & 智慧推薦</h5>
            <p>
              預約平台將透過 AI 主動推薦最適合的服務、預測顧客回訪週期、自動優化排班密度。Beautefeel
              正與客戶一起走在這條路上。
            </p>
          </TimelineItem>
        </TimelineWrap>
      </Section>

      {/* CTA */}
      <CtaWrap>
        <CtaInner>
          <div>
            <h3>別等到產業轉完彎，才發現自己錯過了</h3>
            <p>
              讓我們 30 分鐘免費診斷你的營運現況，告訴你現在最該優先做的 3 件事。
            </p>
          </div>
          <CtaBtn onClick={() => navigate('/contact')}>
            預約營運診斷 <FiArrowRight />
          </CtaBtn>
        </CtaInner>
      </CtaWrap>
    </Page>
  )
}

export default Trends
